import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, MapTrifold, Path, Radio, ArrowsCounterClockwise } from "@phosphor-icons/react";

// Default Leaflet marker icons require manual wiring under bundlers
const TOWER_ICON = new L.DivIcon({
  className: "",
  html: '<div style="width:22px;height:22px;border-radius:50%;background:#0020C2;border:2.5px solid #fff;box-shadow:0 0 0 1.5px #0020C2"></div>',
  iconSize: [22, 22], iconAnchor: [11, 11],
});
const STAR_ICON = new L.DivIcon({
  className: "",
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#C9A84C;border:2px solid #fff;box-shadow:0 0 0 1px #C9A84C"></div>',
  iconSize: [14, 14], iconAnchor: [7, 7],
});

function FitBounds({ assets }) {
  const map = useMap();
  useEffect(() => {
    if (!assets.length) return;
    const pts = [];
    for (const a of assets) {
      if (a.coords) pts.push([a.coords.latitude, a.coords.longitude]);
      if (a.start_coords) pts.push([a.start_coords.latitude, a.start_coords.longitude]);
      if (a.end_coords) pts.push([a.end_coords.latitude, a.end_coords.longitude]);
    }
    if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 14 });
  }, [assets, map]);
  return null;
}

const STATUS_TONE = {
  in_service: "text-emerald-700 bg-emerald-50 border-emerald-300",
  in_stock: "text-gray-600 bg-gray-50 border-gray-300",
  under_maintenance: "text-amber-700 bg-amber-50 border-amber-300",
  in_transit: "text-[#0020C2] bg-blue-50 border-blue-300",
  retired: "text-[#DC2626] bg-red-50 border-red-300",
};

export default function AssetsMapPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grabbing, setGrabbing] = useState(null);   // asset_id currently fetching device GPS
  const [filter, setFilter] = useState("all");      // 'all' | 'point' | 'linear'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alm/assets-map");
      setAssets(data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() =>
    filter === "all" ? assets : assets.filter((a) => a.asset_form === filter),
    [assets, filter]);
  const pointCount = assets.filter((a) => a.asset_form === "point").length;
  const linearCount = assets.filter((a) => a.asset_form === "linear").length;

  const captureMyGps = (assetId) => {
    if (!navigator.geolocation) {
      alert("Your browser doesn't support geolocation.");
      return;
    }
    setGrabbing(assetId);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.put(`/alm/assets/${assetId}/geo`, {
            coords: {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy_m: pos.coords.accuracy,
              captured_at: new Date().toISOString(),
              source: "device_gps",
            },
          });
          await load();
        } finally { setGrabbing(null); }
      },
      (err) => { setGrabbing(null); alert("GPS fix failed: " + err.message); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const dubaiCenter = [25.2048, 55.2708];

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle Management"
        title="Live Asset Map"
        subtitle="Every ALM asset with GPS coordinates — point assets (towers, cabinets, generators) shown as pins, linear assets (fiber spans, cable trenches, pipelines) shown as routes."
        testid="alm-map-title"
        actions={
          <div className="flex gap-1.5">
            {["all", "point", "linear"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`alm-map-filter-${f}`}
                className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider border ${
                  filter === f ? "bg-[#0020C2] text-white border-[#0020C2]" : "bg-white text-gray-700 border-gray-300 hover:border-[#0020C2]"
                }`}>
                {f === "all" ? `All (${assets.length})` : f === "point" ? `Pins (${pointCount})` : `Spans (${linearCount})`}
              </button>
            ))}
            <button onClick={load} data-testid="alm-map-refresh"
              className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider border border-gray-300 bg-white hover:border-[#0020C2] inline-flex items-center gap-1.5">
              <ArrowsCounterClockwise size={11} weight="bold" /> Refresh
            </button>
          </div>
        }
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <div className="border border-gray-200 overflow-hidden" style={{ height: "70vh" }} data-testid="alm-map-canvas">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-500">Loading map…</div>
          ) : (
            <MapContainer center={dubaiCenter} zoom={11} style={{ width: "100%", height: "100%" }}>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds assets={filtered} />
              {filtered.map((a) => {
                if (a.asset_form === "point" && a.coords) {
                  return (
                    <Marker key={a.id} icon={TOWER_ICON} position={[a.coords.latitude, a.coords.longitude]}>
                      <Popup>
                        <div className="text-xs font-mono">
                          <div className="font-bold">{a.asset_tag}</div>
                          <div>{a.status} · {a.coords.source}</div>
                          <div className="text-gray-500">
                            {a.coords.latitude.toFixed(5)}, {a.coords.longitude.toFixed(5)}
                            {a.coords.accuracy_m ? ` · ±${a.coords.accuracy_m.toFixed(1)}m` : ""}
                          </div>
                          <button onClick={() => captureMyGps(a.id)}
                            data-testid={`alm-map-popup-fix-${a.id}`}
                            className="mt-2 inline-flex items-center gap-1 text-[#0020C2] font-bold">
                            <Crosshair size={11} weight="bold" /> Push fresh fix
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  );
                }
                if (a.asset_form === "linear" && a.start_coords && a.end_coords) {
                  const route = (a.route_coords && a.route_coords.length > 0)
                    ? a.route_coords.map((p) => [p.latitude, p.longitude])
                    : [[a.start_coords.latitude, a.start_coords.longitude],
                       [a.end_coords.latitude, a.end_coords.longitude]];
                  return (
                    <React.Fragment key={a.id}>
                      <Polyline positions={route} pathOptions={{ color: "#C9A84C", weight: 4, opacity: 0.85 }}>
                        <Popup>
                          <div className="text-xs font-mono">
                            <div className="font-bold">{a.asset_tag}</div>
                            <div className="text-gray-700">{a.span_label}</div>
                            <div className="text-gray-500">
                              {a.length_m ? `${(a.length_m / 1000).toFixed(2)} km · ` : ""}{a.status}
                            </div>
                          </div>
                        </Popup>
                      </Polyline>
                      <Marker icon={STAR_ICON} position={[a.start_coords.latitude, a.start_coords.longitude]} />
                      <Marker icon={STAR_ICON} position={[a.end_coords.latitude, a.end_coords.longitude]} />
                    </React.Fragment>
                  );
                }
                return null;
              })}
            </MapContainer>
          )}
        </div>
        <div className="border border-gray-200 bg-white" data-testid="alm-map-list">
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Geo-tagged assets</div>
            <h3 className="text-lg font-black tracking-tight mt-0.5">{filtered.length} assets</h3>
          </div>
          <div className="divide-y divide-gray-100 max-h-[64vh] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No geo-tagged assets yet. Add latitude/longitude on an asset to see it here.
              </div>
            )}
            {filtered.map((a) => (
              <div key={a.id} className="px-4 py-3" data-testid={`alm-map-row-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-sm flex items-center gap-1.5">
                      {a.asset_form === "linear" ? <Path size={12} weight="bold" className="text-[#C9A84C]" /> : <Radio size={12} weight="fill" className="text-[#0020C2]" />}
                      {a.asset_tag}
                    </div>
                    {a.span_label && <div className="text-[11px] text-gray-600 mt-0.5 truncate">{a.span_label}</div>}
                    <div className="text-[10px] font-mono text-gray-500 mt-1">
                      {a.asset_form === "point" && a.coords
                        ? `${a.coords.latitude.toFixed(5)}, ${a.coords.longitude.toFixed(5)}`
                        : a.start_coords && a.end_coords
                          ? `${a.start_coords.latitude.toFixed(3)},${a.start_coords.longitude.toFixed(3)} → ${a.end_coords.latitude.toFixed(3)},${a.end_coords.longitude.toFixed(3)}`
                          : ""}
                      {a.length_m ? ` · ${(a.length_m / 1000).toFixed(2)} km` : ""}
                    </div>
                  </div>
                  <span className={`shrink-0 text-[10px] font-bold tracking-wider uppercase border px-1.5 py-0.5 ${STATUS_TONE[a.status] || STATUS_TONE.in_stock}`}>
                    {a.status?.replace("_", " ") || "—"}
                  </span>
                </div>
                {a.asset_form === "point" && (
                  <button onClick={() => captureMyGps(a.id)} disabled={grabbing === a.id}
                    data-testid={`alm-map-grab-${a.id}`}
                    className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#0020C2] hover:underline disabled:opacity-50">
                    <Crosshair size={11} weight="bold" /> {grabbing === a.id ? "Capturing…" : "Push fresh GPS fix"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
